import React, { useState, useEffect, useMemo } from "react";
import { Download, Search, ChevronUp, ChevronDown, Layers } from "lucide-react";
import PeriodSelector from "../components/PeriodSelector.jsx";
import { exportCommissionReportToExcel } from "../components/ExcelExport.jsx";
import { Link } from "react-router-dom";
import api from "../services/api"; // Centralized authenticated Axios instance

// ─── Constants ──────────────────────────────────────────────────────────────────
const TABS = ["Manager", "Assistant Manager", "Employee"];

// ─── Formatters ─────────────────────────────────────────────────────────────────
const fmt = (num) =>
  Number(num || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtCur = (num) => `$${fmt(num)}`;
const fmtPct = (num) => `${Number(num || 0).toFixed(2)}%`;

const fmtPctAcct = (num, digits = 2) => {
  const v = Number(num || 0);
  const s = `${Math.abs(v).toFixed(digits)}%`;
  return v < 0 ? `(${s})` : s;
};

// ─── API Service ─────────────────────────────────────────────────────────────────
const fetchReport = async ({ year, month, quarter, ytd }) => {
  const params = { year };
  if (month !== null && month !== undefined) params.month = month;
  if (quarter) params.q = quarter;
  if (ytd) params.ytd = "true";

  // Fixed endpoint: calls http://10.5.21.42:5000/api/store/commissions
  const res = await api.get("/store/commissions", { params });
  return res.data.data;
};

const isCoMgrStore = (store) =>
  (store.employees || []).filter((e) => e.role === "Manager").length > 1;

// ─── Row builder ─────────────────────────────────────────────────────────────────
const buildRows = (locations, activeTab, selectedStoreId) => {
  const rows = [];

  locations.forEach((store) => {
    if (
      selectedStoreId !== "all" &&
      String(store.storeId) !== String(selectedStoreId)
    )
      return;

    const hasCoMgr = isCoMgrStore(store);
    const isManagerTab = activeTab === "Manager";
    const divisor = hasCoMgr && isManagerTab ? 2 : 1;

    const storeSale = (store.totalStoreSale ?? 0) / divisor;
    const quota = (store.quota ?? 0) / divisor;
    const rawStoreSale = store.totalStoreSale ?? 0;
    const rawQuota = store.quota ?? 0;

    const storeLetterPrefix =
      store.storeId >= 1 && store.storeId <= 26
        ? String.fromCharCode(64 + store.storeId).toUpperCase()
        : null;

    const isNativeEmployee = (emp) => {
      const firstChar = String(emp.employeeId || "")[0]?.toUpperCase() ?? "";
      if (/[0-9]/.test(firstChar)) {
        return selectedStoreId === "all";
      }
      return storeLetterPrefix === null || firstChar === storeLetterPrefix;
    };

    const matchingEmps = (store.employees || []).filter(
      (e) => e.role === activeTab && isNativeEmployee(e),
    );

    if (matchingEmps.length === 0) {
      rows.push({
        storeId: store.storeId,
        storeName: store.storeName,
        employeeId: "",
        pbsId: "", // Vacant row fallback
        name: null,
        role: activeTab,
        hasCoMgr,
        isPlaceholder: true,
        tierName: store.tier ?? "—",
        storeSale,
        quota,
        quotaAchieved: store.quotaAchievedPercent ?? null,
        storeUpDownPercent: store.storeUpDownPercent ?? null,
        rawStoreSale,
        rawQuota,
        storeCreditRate: null,
        storeCreditAmt: 0,
        salesmanSale: 0,
        salesmanRate: null,
        salesmanCommissionAmt: 0,
        totalCommission: 0,
      });
      return;
    }

    matchingEmps.forEach((emp) => {
      const storeCreditAmt =
        isManagerTab && hasCoMgr
          ? (emp.storeCreditAmt ?? 0) / divisor
          : (emp.storeCreditAmt ?? 0);

      rows.push({
        storeId: store.storeId,
        storeName: store.storeName,
        employeeId: emp.employeeId,
        pbsId: emp.pbsId || emp.pbs_id || "",
        name: emp.name || null,
        role: emp.role,
        hasCoMgr,
        isPlaceholder: false,
        tierName: store.tier ?? "—",
        storeSale,
        quota,
        quotaAchieved: store.quotaAchievedPercent ?? null,
        storeUpDownPercent: store.storeUpDownPercent ?? null,
        rawStoreSale,
        rawQuota,
        storeCreditRate: emp.storeCreditRate ?? null,
        storeCreditAmt,
        salesmanSale: emp.salesmanSale ?? 0,
        salesmanRate: emp.salesmanRate ?? null,
        salesmanCommissionAmt: emp.salesmanCommissionAmt ?? 0,
        totalCommission: storeCreditAmt + (emp.salesmanCommissionAmt ?? 0),
      });
    });
  });

  return rows;
};

const EmDash = () => <span className="text-slate-300">—</span>;

const UpDownText = ({ percent }) => {
  if (percent === null || percent === undefined) return <EmDash />;
  const val = Number(percent);
  return (
    <span className={val < 0 ? "text-red-600 font-medium" : "text-slate-700"}>
      {fmtPctAcct(val)}
    </span>
  );
};

const QuotaText = ({ percent }) => {
  if (percent === null || percent === undefined) return <EmDash />;
  const val = Number(percent);
  return (
    <span
      className={val >= 99 ? "text-emerald-800 font-bold" : "text-slate-700"}
    >
      {val.toFixed(1)}%
    </span>
  );
};

const TierText = ({ tier }) => {
  if (!tier || tier === "—") return <EmDash />;
  return <span className="text-slate-700 font-medium">{tier}</span>;
};

// ─── Main Component ──────────────────────────────────────────────────────────────
const StoreCommissions = () => {
  const now = new Date();
  const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1);

  const [year, setYear] = useState(prevMonth.getFullYear());
  const [period, setPeriod] = useState(`month-${prevMonth.getMonth() + 1}`);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Manager");
  const [selectedStoreId, setSelectedStoreId] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  useEffect(() => {
    setLoading(true);
    let ytd = false,
      m,
      q;
    if (period === "ytd") ytd = true;
    else if (period.startsWith("month-")) m = Number(period.split("-")[1]);
    else if (period.startsWith("quarter-")) q = period.split("-")[1];

    fetchReport({ year, month: m, quarter: q, ytd })
      .then((data) => setReport(data))
      .catch((err) => {
        console.error("Failed to load store commission report:", err);
        setReport(null);
      })
      .finally(() => setLoading(false));
  }, [year, period]);

  useEffect(() => {
    setSelectedStoreId("all");
  }, [activeTab]);

  const locations = useMemo(() => report?.locations || [], [report]);

  const allRows = useMemo(
    () => buildRows(locations, activeTab, selectedStoreId),
    [locations, activeTab, selectedStoreId],
  );

  const searchedRows = useMemo(() => {
    if (!searchTerm.trim()) return allRows;
    const t = searchTerm.toLowerCase();
    return allRows.filter(
      (r) =>
        (r.name || "").toLowerCase().includes(t) ||
        r.storeName.toLowerCase().includes(t) ||
        r.employeeId.toLowerCase().includes(t) ||
        (r.pbsId || "").toLowerCase().includes(t) ||
        String(r.storeId).includes(t),
    );
  }, [allRows, searchTerm]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return searchedRows;
    return [...searchedRows].sort((a, b) => {
      let av = a[sortConfig.key],
        bv = b[sortConfig.key];
      if (typeof av === "number" || typeof bv === "number") {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      } else {
        av = String(av ?? "").toLowerCase();
        bv = String(bv ?? "").toLowerCase();
      }
      if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
      if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [searchedRows, sortConfig]);

  const totals = useMemo(() => {
    const seenStores = new Set();
    let storeSaleSum = 0,
      quotaSum = 0;
    let storeCreditSum = 0,
      salesmanSaleSum = 0;
    let salesmanCommSum = 0,
      totalCommSum = 0;

    sortedRows.forEach((r) => {
      if (!seenStores.has(r.storeId)) {
        storeSaleSum += r.rawStoreSale;
        quotaSum += r.rawQuota;
        seenStores.add(r.storeId);
      }
      storeCreditSum += r.storeCreditAmt;
      salesmanSaleSum += r.salesmanSale;
      salesmanCommSum += r.salesmanCommissionAmt;
      totalCommSum += r.totalCommission;
    });

    const avgQA = quotaSum > 0 ? (storeSaleSum / quotaSum) * 100 : 0;

    return {
      storeSaleSum,
      quotaSum,
      storeCreditSum,
      salesmanSaleSum,
      salesmanCommSum,
      totalCommSum,
      avgQA,
    };
  }, [sortedRows]);

  const tabCounts = useMemo(() => {
    const counts = { Manager: 0, "Assistant Manager": 0, Employee: 0 };
    locations.forEach((s) =>
      (s.employees || []).forEach((e) => {
        if (counts[e.role] !== undefined) counts[e.role]++;
      }),
    );
    return counts;
  }, [locations]);

  const handleSort = (key) =>
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey)
      return (
        <ChevronUp className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100" />
      );
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="w-3.5 h-3.5 text-slate-800" />
    ) : (
      <ChevronDown className="w-3.5 h-3.5 text-slate-800" />
    );
  };

  const Th = ({ colKey, children, right = false }) => (
    <th
      onClick={() => handleSort(colKey)}
      className={`group sticky top-0 z-20 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap border-b border-slate-200 ${right ? "text-right" : "text-left"}`}
    >
      <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
        {children}
        <SortIcon columnKey={colKey} />
      </div>
    </th>
  );

  const exportToExcel = () => {
    if (!sortedRows.length) return;
    exportCommissionReportToExcel(
      sortedRows.map((r) => ({
        storeId: r.storeId,
        employeeId: r.employeeId,
        pbsId: r.pbsId,
        activeTab: activeTab,
        name: r.name || "",
        role: r.role,
        tier: r.tierName,
        storeSale: r.storeSale,
        quota: r.quota,
        rawStoreSale: r.rawStoreSale,
        rawQuota: r.rawQuota / 100,
        quotaAchieved: r.quotaAchieved / 100,
        storeUpDownPercent: r.storeUpDownPercent / 100,
        storeCreditRate: r.storeCreditRate / 100,
        storeCreditAmt: r.storeCreditAmt,
        salesmanSale: r.salesmanSale,
        salesmanRate: r.salesmanRate / 100,
        salesmanCommissionAmt: r.salesmanCommissionAmt,
        totalCommission: r.totalCommission,
      })),
    );
  };

  let periodLabel = "";
  if (period === "ytd") periodLabel = `Year to Date ${year}`;
  else if (period.startsWith("month-")) {
    const m = Number(period.split("-")[1]);
    periodLabel = `${new Date(year, m - 1).toLocaleString("default", { month: "long" })} ${year}`;
  } else if (period.startsWith("quarter-")) {
    periodLabel = `${period.split("-")[1].toUpperCase()} ${year}`;
  }

  const isManagerTab = activeTab === "Manager";
  const hasAnyCoMgr = locations.some(isCoMgrStore);

  return (
    <div className="min-h-screen bg-slate-50 py-6 text-slate-900 font-sans">
      <div className="w-full px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="bg-white rounded border border-slate-200 px-5 py-4 shadow-sm">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="mr-auto">
              <h1 className="text-xl font-bold tracking-tight text-slate-900">
                Store Commissions
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {periodLabel
                  ? `${periodLabel} Performance Report`
                  : `${year} Performance Report`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Year
              </label>
              <input
                type="number"
                min="2000"
                max="2100"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="border border-slate-300 rounded px-2 py-1 w-24 text-base font-medium focus:outline-none focus:border-slate-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Period
              </label>
              <PeriodSelector period={period} setPeriod={setPeriod} />
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search name, store, or ID…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-slate-300 rounded pl-9 pr-4 py-1.5 text-sm focus:outline-none focus:border-slate-500 bg-white"
              />
            </div>
            <div className="h-6 w-px bg-slate-200 hidden lg:block" />
            <Link
              to="/store-comm-tiers"
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
            >
              <Layers className="w-4 h-4" /> Tiers
            </Link>
            <button
              onClick={exportToExcel}
              disabled={!report || sortedRows.length === 0}
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded text-sm font-semibold uppercase tracking-wider transition"
            >
              <Download className="w-4 h-4" /> Export
            </button>
          </div>
        </div>

        {loading && (
          <div className="bg-white rounded border border-slate-200 p-12 text-center shadow-sm">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-slate-800 border-t-transparent" />
            <p className="mt-3 text-slate-600 text-sm font-medium">
              Loading report records…
            </p>
          </div>
        )}

        {!loading && !report && (
          <div className="bg-white rounded border border-red-200 p-12 text-center shadow-sm">
            <p className="text-red-600 font-bold text-base">
              Failed to load system ledger report
            </p>
            <p className="text-slate-500 mt-0.5 text-sm">
              Please verify network connectivity status or retry transaction
              view.
            </p>
          </div>
        )}

        {!loading && report && (
          <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex flex-wrap items-center justify-between border-b border-slate-200 bg-slate-50 px-2">
              <div className="flex overflow-x-auto">
                {TABS.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
                      activeTab === tab
                        ? "border-slate-800 text-slate-900 bg-white"
                        : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                    }`}
                  >
                    {tab === "Assistant Manager" ? "Asst. Managers" : `${tab}s`}
                    <span
                      className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold tracking-normal ${activeTab === tab ? "bg-slate-800 text-white" : "bg-slate-200 text-slate-600"}`}
                    >
                      {tabCounts[tab] || 0}
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-3 py-2 px-4 ml-auto">
                <select
                  value={selectedStoreId}
                  onChange={(e) => setSelectedStoreId(e.target.value)}
                  className="border border-slate-300 rounded px-3 py-1 text-sm bg-white font-medium focus:outline-none focus:border-slate-500"
                >
                  <option value="all">All Stores</option>
                  {locations.map((s) => (
                    <option key={s.storeId} value={String(s.storeId)}>
                      Store {s.storeId}
                    </option>
                  ))}
                </select>
                <span className="text-sm font-mono text-slate-500 whitespace-nowrap">
                  Count: {sortedRows.length} Records
                </span>
              </div>
            </div>

            {isManagerTab && hasAnyCoMgr && (
              <div className="text-xs text-slate-600 bg-slate-100 border-b border-slate-200 px-6 py-2">
                <span className="font-bold uppercase tracking-wider text-slate-700 mr-1">
                  Co-manager stores:
                </span>
                Store sales, quota, and store credits are displayed split (÷2)
                per manager row inside this ledger.
              </div>
            )}

            <div className="overflow-auto max-h-[75vh]">
              <table className="w-full border-collapse font-mono text-base">
                <thead>
                  <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50/70">
                    <th
                      colSpan={2}
                      className="px-4 py-1.5 text-left border-r border-slate-200"
                    >
                      Identity Details
                    </th>
                    <th
                      colSpan={4}
                      className="px-4 py-1.5 text-center border-r border-slate-200"
                    >
                      Store Performance Status{" "}
                      {isManagerTab && hasAnyCoMgr ? " (÷2 Co-Mgr)" : ""}
                    </th>
                    <th
                      colSpan={2}
                      className="px-4 py-1.5 text-center border-r border-slate-200"
                    >
                      Store Credit Pool
                    </th>
                    <th
                      colSpan={3}
                      className="px-4 py-1.5 text-center border-r border-slate-200"
                    >
                      {activeTab === "Manager"
                        ? "MGR"
                        : activeTab === "Assistant Manager"
                          ? "ASST MGR"
                          : "Personal"}{" "}
                      Sales
                    </th>
                    <th colSpan={1} className="px-4 py-1.5 text-right">
                      Payout
                    </th>
                  </tr>
                  <tr className="bg-slate-50 border-b border-slate-300 sticky top-0 z-20">
                    <Th colKey="storeId">Store</Th>
                    <Th colKey="name">Employee</Th>
                    <Th colKey="storeSale" right>
                      Store Sales
                    </Th>
                    <Th colKey="quota" right>
                      Quota
                    </Th>
                    <Th colKey="quotaAchieved" right>
                      Quota %
                    </Th>
                    <Th colKey="storeUpDownPercent" right>
                      Up/Down
                    </Th>
                    <Th colKey="tierName">Tier</Th>
                    <Th colKey="storeCreditRate" right>
                      Rate
                    </Th>
                    <Th colKey="storeCreditAmt" right>
                      Credit Amt
                    </Th>
                    <Th colKey="salesmanSale" right>
                      Sales Vol
                    </Th>
                    <Th colKey="salesmanRate" right>
                      Rate
                    </Th>
                    <Th colKey="salesmanCommissionAmt" right>
                      Comm Amt
                    </Th>
                    <Th colKey="totalCommission" right>
                      Net Total
                    </Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800">
                  {sortedRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={13}
                        className="px-4 py-14 text-center text-slate-400 text-lg font-sans"
                      >
                        No {activeTab.toLowerCase()} accounts returned under
                        execution criteria.
                      </td>
                    </tr>
                  ) : (
                    sortedRows.map((r, i) => {
                      const isHit = Number(r.quotaAchieved) >= 99;
                      const isCoMgrRow = r.hasCoMgr && isManagerTab;
                      return (
                        <tr
                          key={`${r.storeId}-${r.employeeId || "none"}-${i}`}
                          className={`transition-colors ${isHit ? "bg-emerald-50/60 hover:bg-emerald-50" : isCoMgrRow ? "bg-slate-100/40 hover:bg-slate-100/70" : "hover:bg-slate-50"} ${r.isPlaceholder ? "opacity-50" : ""}`}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-900">
                                {r.storeId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {r.isPlaceholder ? (
                              <span className="text-sm text-slate-400 italic font-sans">
                                Vacant Asset Assignment
                              </span>
                            ) : (
                              <>
                                <div
                                  className={
                                    !r.name
                                      ? "text-slate-400 italic font-sans"
                                      : "font-sans font-medium text-slate-900"
                                  }
                                >
                                  {r.name || "—"}{" "}
                                  {isCoMgrRow && !isHit && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-slate-200 text-slate-600 ml-1">
                                      co
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-slate-400 tracking-wide mt-0.5">
                                  {r.employeeId}{" "}
                                  {r.pbsId && `| PBS: ${r.pbsId}`}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-900">
                            {fmtCur(r.storeSale)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {fmtCur(r.quota)}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            <QuotaText percent={r.quotaAchieved} />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            <UpDownText percent={r.storeUpDownPercent} />
                          </td>
                          <td className="px-4 py-3 text-center tabular-nums">
                            <TierText tier={r.tierName} />
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {r.isPlaceholder || r.storeCreditRate === null ? (
                              <EmDash />
                            ) : (
                              fmtPct(r.storeCreditRate)
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-700">
                            {r.isPlaceholder ? (
                              <EmDash />
                            ) : (
                              fmtCur(r.storeCreditAmt)
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {r.isPlaceholder ? (
                              <EmDash />
                            ) : (
                              fmtCur(r.salesmanSale)
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {r.isPlaceholder || r.salesmanRate === null ? (
                              <EmDash />
                            ) : (
                              fmtPct(r.salesmanRate)
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                            {r.isPlaceholder ? (
                              <EmDash />
                            ) : (
                              fmtCur(r.salesmanCommissionAmt)
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                            {fmtCur(r.totalCommission)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50/50 border-t-2 border-slate-400 text-slate-900 font-bold">
                    <td
                      colSpan={2}
                      className="px-4 py-3 font-sans text-slate-700 font-bold"
                    >
                      Total Aggregates
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {fmtCur(totals.storeSaleSum)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {fmtCur(totals.quotaSum)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-extrabold text-slate-950">
                      {totals.avgQA.toFixed(2)}%
                    </td>
                    <td colSpan={2} />
                    <td className="px-4 py-3 text-right text-xs uppercase text-slate-400 font-sans tracking-wider">
                      Subtotals
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {fmtCur(totals.storeCreditSum)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                      {fmtCur(totals.salesmanSaleSum)}
                    </td>
                    <td colSpan={1} />
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                      {fmtCur(totals.salesmanCommSum)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-950 border-b-4 border-double border-slate-900 bg-slate-50">
                      {fmtCur(totals.totalCommSum)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreCommissions;

// import React, { useState, useEffect, useMemo } from "react";
// import { Download, Search, ChevronUp, ChevronDown, Layers } from "lucide-react";
// import PeriodSelector from "../components/PeriodSelector.jsx";
// import { exportCommissionReportToExcel } from "../components/ExcelExport.jsx";
// import { Link } from "react-router-dom";

// // ─── Constants ──────────────────────────────────────────────────────────────────
// const TABS = ["Manager", "Assistant Manager", "Employee"];

// // ─── Formatters ─────────────────────────────────────────────────────────────────
// const fmt = (num) =>
//   Number(num || 0).toLocaleString(undefined, {
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2,
//   });
// const fmtCur = (num) => `$${fmt(num)}`;
// const fmtPct = (num) => `${Number(num || 0).toFixed(2)}%`;

// const fmtPctAcct = (num, digits = 2) => {
//   const v = Number(num || 0);
//   const s = `${Math.abs(v).toFixed(digits)}%`;
//   return v < 0 ? `(${s})` : s;
// };

// // ─── API ─────────────────────────────────────────────────────────────────────────
// const fetchReport = async ({ year, month, quarter, ytd }) => {
//   const params = new URLSearchParams({ year });
//   if (month !== null && month !== undefined) params.append("month", month);
//   if (quarter) params.append("q", quarter);
//   if (ytd) params.append("ytd", "true");
//   const res = await fetch(
//     `http://10.5.21.42:5000/api/store/commissions?${params.toString()}`,
//   );
//   if (!res.ok) throw new Error("Failed to fetch commission report");
//   const json = await res.json();
//   return json.data;
// };

// const isCoMgrStore = (store) =>
//   (store.employees || []).filter((e) => e.role === "Manager").length > 1;

// // ─── Row builder ─────────────────────────────────────────────────────────────────
// const buildRows = (locations, activeTab, selectedStoreId) => {
//   const rows = [];

//   locations.forEach((store) => {
//     if (
//       selectedStoreId !== "all" &&
//       String(store.storeId) !== String(selectedStoreId)
//     )
//       return;

//     const hasCoMgr = isCoMgrStore(store);
//     const isManagerTab = activeTab === "Manager";
//     const divisor = hasCoMgr && isManagerTab ? 2 : 1;

//     const storeSale = (store.totalStoreSale ?? 0) / divisor;
//     const quota = (store.quota ?? 0) / divisor;
//     const rawStoreSale = store.totalStoreSale ?? 0;
//     const rawQuota = store.quota ?? 0;

//     const storeLetterPrefix =
//       store.storeId >= 1 && store.storeId <= 26
//         ? String.fromCharCode(64 + store.storeId).toUpperCase()
//         : null;

//     const isNativeEmployee = (emp) => {
//       const firstChar = String(emp.employeeId || "")[0]?.toUpperCase() ?? "";
//       if (/[0-9]/.test(firstChar)) {
//         return selectedStoreId === "all";
//       }
//       return storeLetterPrefix === null || firstChar === storeLetterPrefix;
//     };

//     const matchingEmps = (store.employees || []).filter(
//       (e) => e.role === activeTab && isNativeEmployee(e),
//     );

//     if (matchingEmps.length === 0) {
//       rows.push({
//         storeId: store.storeId,
//         storeName: store.storeName,
//         employeeId: "",
//         pbsId: "", // Vacant row fallback
//         name: null,
//         role: activeTab,
//         hasCoMgr,
//         isPlaceholder: true,
//         tierName: store.tier ?? "—",
//         storeSale,
//         quota,
//         quotaAchieved: store.quotaAchievedPercent ?? null,
//         storeUpDownPercent: store.storeUpDownPercent ?? null,
//         rawStoreSale,
//         rawQuota,
//         storeCreditRate: null,
//         storeCreditAmt: 0,
//         salesmanSale: 0,
//         salesmanRate: null,
//         salesmanCommissionAmt: 0,
//         totalCommission: 0,
//       });
//       return;
//     }

//     matchingEmps.forEach((emp) => {
//       const storeCreditAmt =
//         isManagerTab && hasCoMgr
//           ? (emp.storeCreditAmt ?? 0) / divisor
//           : (emp.storeCreditAmt ?? 0);

//       rows.push({
//         storeId: store.storeId,
//         storeName: store.storeName,
//         employeeId: emp.employeeId,
//         pbsId: emp.pbsId || emp.pbs_id || "", // Safe extraction of pbs_id from backend payload
//         name: emp.name || null,
//         role: emp.role,
//         hasCoMgr,
//         isPlaceholder: false,
//         tierName: store.tier ?? "—",
//         storeSale,
//         quota,
//         quotaAchieved: store.quotaAchievedPercent ?? null,
//         storeUpDownPercent: store.storeUpDownPercent ?? null,
//         rawStoreSale,
//         rawQuota,
//         storeCreditRate: emp.storeCreditRate ?? null,
//         storeCreditAmt,
//         salesmanSale: emp.salesmanSale ?? 0,
//         salesmanRate: emp.salesmanRate ?? null,
//         salesmanCommissionAmt: emp.salesmanCommissionAmt ?? 0,
//         totalCommission: storeCreditAmt + (emp.salesmanCommissionAmt ?? 0),
//       });
//     });
//   });

//   return rows;
// };

// const EmDash = () => <span className="text-slate-300">—</span>;

// const UpDownText = ({ percent }) => {
//   if (percent === null || percent === undefined) return <EmDash />;
//   const val = Number(percent);
//   return (
//     <span className={val < 0 ? "text-red-600 font-medium" : "text-slate-700"}>
//       {fmtPctAcct(val)}
//     </span>
//   );
// };

// const QuotaText = ({ percent }) => {
//   if (percent === null || percent === undefined) return <EmDash />;
//   const val = Number(percent);
//   return (
//     <span
//       className={val >= 99 ? "text-emerald-800 font-bold" : "text-slate-700"}
//     >
//       {val.toFixed(1)}%
//     </span>
//   );
// };

// const TierText = ({ tier }) => {
//   if (!tier || tier === "—") return <EmDash />;
//   return <span className="text-slate-700 font-medium">{tier}</span>;
// };

// // ─── Main Component ──────────────────────────────────────────────────────────────
// const StoreCommissions = () => {
//   const now = new Date();
//   const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1);

//   const [year, setYear] = useState(prevMonth.getFullYear());
//   const [period, setPeriod] = useState(`month-${prevMonth.getMonth() + 1}`);
//   const [report, setReport] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [activeTab, setActiveTab] = useState("Manager");
//   const [selectedStoreId, setSelectedStoreId] = useState("all");
//   const [searchTerm, setSearchTerm] = useState("");
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

//   useEffect(() => {
//     setLoading(true);
//     let ytd = false,
//       m,
//       q;
//     if (period === "ytd") ytd = true;
//     else if (period.startsWith("month-")) m = Number(period.split("-")[1]);
//     else if (period.startsWith("quarter-")) q = period.split("-")[1];

//     fetchReport({ year, month: m, quarter: q, ytd })
//       .then((data) => setReport(data))
//       .catch((err) => {
//         console.error(err);
//         setReport(null);
//       })
//       .finally(() => setLoading(false));
//   }, [year, period]);

//   useEffect(() => {
//     setSelectedStoreId("all");
//   }, [activeTab]);

//   const locations = useMemo(() => report?.locations || [], [report]);

//   const allRows = useMemo(
//     () => buildRows(locations, activeTab, selectedStoreId),
//     [locations, activeTab, selectedStoreId],
//   );

//   const searchedRows = useMemo(() => {
//     if (!searchTerm.trim()) return allRows;
//     const t = searchTerm.toLowerCase();
//     return allRows.filter(
//       (r) =>
//         (r.name || "").toLowerCase().includes(t) ||
//         r.storeName.toLowerCase().includes(t) ||
//         r.employeeId.toLowerCase().includes(t) ||
//         (r.pbsId || "").toLowerCase().includes(t) ||
//         String(r.storeId).includes(t),
//     );
//   }, [allRows, searchTerm]);

//   const sortedRows = useMemo(() => {
//     if (!sortConfig.key) return searchedRows;
//     return [...searchedRows].sort((a, b) => {
//       let av = a[sortConfig.key],
//         bv = b[sortConfig.key];
//       if (typeof av === "number" || typeof bv === "number") {
//         av = Number(av) || 0;
//         bv = Number(bv) || 0;
//       } else {
//         av = String(av ?? "").toLowerCase();
//         bv = String(bv ?? "").toLowerCase();
//       }
//       if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
//       if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
//       return 0;
//     });
//   }, [searchedRows, sortConfig]);

//   const totals = useMemo(() => {
//     const seenStores = new Set();
//     let storeSaleSum = 0,
//       quotaSum = 0;
//     let storeCreditSum = 0,
//       salesmanSaleSum = 0;
//     let salesmanCommSum = 0,
//       totalCommSum = 0;

//     sortedRows.forEach((r) => {
//       if (!seenStores.has(r.storeId)) {
//         storeSaleSum += r.rawStoreSale;
//         quotaSum += r.rawQuota;
//         seenStores.add(r.storeId);
//       }
//       storeCreditSum += r.storeCreditAmt;
//       salesmanSaleSum += r.salesmanSale;
//       salesmanCommSum += r.salesmanCommissionAmt;
//       totalCommSum += r.totalCommission;
//     });

//     const avgQA = quotaSum > 0 ? (storeSaleSum / quotaSum) * 100 : 0;

//     return {
//       storeSaleSum,
//       quotaSum,
//       storeCreditSum,
//       salesmanSaleSum,
//       salesmanCommSum,
//       totalCommSum,
//       avgQA,
//     };
//   }, [sortedRows]);

//   const tabCounts = useMemo(() => {
//     const counts = { Manager: 0, "Assistant Manager": 0, Employee: 0 };
//     locations.forEach((s) =>
//       (s.employees || []).forEach((e) => {
//         if (counts[e.role] !== undefined) counts[e.role]++;
//       }),
//     );
//     return counts;
//   }, [locations]);

//   const handleSort = (key) =>
//     setSortConfig((prev) => ({
//       key,
//       direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
//     }));

//   const SortIcon = ({ columnKey }) => {
//     if (sortConfig.key !== columnKey)
//       return (
//         <ChevronUp className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100" />
//       );
//     return sortConfig.direction === "asc" ? (
//       <ChevronUp className="w-3.5 h-3.5 text-slate-800" />
//     ) : (
//       <ChevronDown className="w-3.5 h-3.5 text-slate-800" />
//     );
//   };

//   const Th = ({ colKey, children, right = false }) => (
//     <th
//       onClick={() => handleSort(colKey)}
//       className={`group sticky top-0 z-20 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap border-b border-slate-200 ${right ? "text-right" : "text-left"}`}
//     >
//       <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
//         {children}
//         <SortIcon columnKey={colKey} />
//       </div>
//     </th>
//   );

//   // ── Export Map Updated ───────────────────────────────────────────────────────
//   const exportToExcel = () => {
//     if (!sortedRows.length) return;
//     exportCommissionReportToExcel(
//       sortedRows.map((r) => ({
//         storeId: r.storeId,
//         employeeId: r.employeeId,
//         pbsId: r.pbsId, // Added right here so it safely passes to the exporter mapping
//         activeTab: activeTab,
//         name: r.name || "",
//         role: r.role,
//         tier: r.tierName,
//         storeSale: r.storeSale,
//         quota: r.quota,
//         rawStoreSale: r.rawStoreSale,
//         rawQuota: r.rawQuota / 100,
//         quotaAchieved: r.quotaAchieved / 100,
//         storeUpDownPercent: r.storeUpDownPercent / 100,
//         storeCreditRate: r.storeCreditRate / 100,
//         storeCreditAmt: r.storeCreditAmt,
//         salesmanSale: r.salesmanSale,
//         salesmanRate: r.salesmanRate / 100,
//         salesmanCommissionAmt: r.salesmanCommissionAmt,
//         totalCommission: r.totalCommission,
//       })),
//     );
//   };

//   let periodLabel = "";
//   if (period === "ytd") periodLabel = `Year to Date ${year}`;
//   else if (period.startsWith("month-")) {
//     const m = Number(period.split("-")[1]);
//     periodLabel = `${new Date(year, m - 1).toLocaleString("default", { month: "long" })} ${year}`;
//   } else if (period.startsWith("quarter-")) {
//     periodLabel = `${period.split("-")[1].toUpperCase()} ${year}`;
//   }

//   const isManagerTab = activeTab === "Manager";
//   const hasAnyCoMgr = locations.some(isCoMgrStore);

//   return (
//     <div className="min-h-screen bg-slate-50 py-6 text-slate-900 font-sans">
//       <div className="w-full px-4 sm:px-6 lg:px-8 space-y-4">
//         <div className="bg-white rounded border border-slate-200 px-5 py-4 shadow-sm">
//           <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
//             <div className="mr-auto">
//               <h1 className="text-xl font-bold tracking-tight text-slate-900">
//                 Store Commissions
//               </h1>
//               <p className="text-slate-500 text-sm mt-0.5">
//                 {periodLabel
//                   ? `${periodLabel} Performance Report`
//                   : `${year} Performance Report`}
//               </p>
//             </div>
//             <div className="flex items-center gap-2">
//               <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
//                 Year
//               </label>
//               <input
//                 type="number"
//                 min="2000"
//                 max="2100"
//                 value={year}
//                 onChange={(e) => setYear(Number(e.target.value))}
//                 className="border border-slate-300 rounded px-2 py-1 w-24 text-base font-medium focus:outline-none focus:border-slate-500"
//               />
//             </div>
//             <div className="flex items-center gap-2">
//               <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
//                 Period
//               </label>
//               <PeriodSelector period={period} setPeriod={setPeriod} />
//             </div>
//             <div className="relative w-64">
//               <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
//               <input
//                 type="text"
//                 placeholder="Search name, store, or ID…"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 className="w-full border border-slate-300 rounded pl-9 pr-4 py-1.5 text-sm focus:outline-none focus:border-slate-500 bg-white"
//               />
//             </div>
//             <div className="h-6 w-px bg-slate-200 hidden lg:block" />
//             <Link
//               to="/store-comm-tiers"
//               className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold uppercase tracking-wider text-slate-700 bg-slate-100 hover:bg-slate-200 rounded transition"
//             >
//               <Layers className="w-4 h-4" /> Tiers
//             </Link>
//             <button
//               onClick={exportToExcel}
//               disabled={!report || sortedRows.length === 0}
//               className="flex items-center gap-1.5 px-3 py-2 bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-white rounded text-sm font-semibold uppercase tracking-wider transition"
//             >
//               <Download className="w-4 h-4" /> Export
//             </button>
//           </div>
//         </div>

//         {loading && (
//           <div className="bg-white rounded border border-slate-200 p-12 text-center shadow-sm">
//             <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-slate-800 border-t-transparent" />
//             <p className="mt-3 text-slate-600 text-sm font-medium">
//               Loading report records…
//             </p>
//           </div>
//         )}

//         {!loading && !report && (
//           <div className="bg-white rounded border border-red-200 p-12 text-center shadow-sm">
//             <p className="text-red-600 font-bold text-base">
//               Failed to load system ledger report
//             </p>
//             <p className="text-slate-500 mt-0.5 text-sm">
//               Please verify network connectivity status or retry transaction
//               view.
//             </p>
//           </div>
//         )}

//         {!loading && report && (
//           <div className="bg-white rounded border border-slate-200 overflow-hidden shadow-sm">
//             <div className="flex flex-wrap items-center justify-between border-b border-slate-200 bg-slate-50 px-2">
//               <div className="flex overflow-x-auto">
//                 {TABS.map((tab) => (
//                   <button
//                     key={tab}
//                     onClick={() => setActiveTab(tab)}
//                     className={`flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-wider border-b-2 transition-all ${
//                       activeTab === tab
//                         ? "border-slate-800 text-slate-900 bg-white"
//                         : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100"
//                     }`}
//                   >
//                     {tab === "Assistant Manager" ? "Asst. Managers" : `${tab}s`}
//                     <span
//                       className={`inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-bold tracking-normal ${activeTab === tab ? "bg-slate-800 text-white" : "bg-slate-200 text-slate-600"}`}
//                     >
//                       {tabCounts[tab] || 0}
//                     </span>
//                   </button>
//                 ))}
//               </div>
//               <div className="flex items-center gap-3 py-2 px-4 ml-auto">
//                 <select
//                   value={selectedStoreId}
//                   onChange={(e) => setSelectedStoreId(e.target.value)}
//                   className="border border-slate-300 rounded px-3 py-1 text-sm bg-white font-medium focus:outline-none focus:border-slate-500"
//                 >
//                   <option value="all">All Stores</option>
//                   {locations.map((s) => (
//                     <option key={s.storeId} value={String(s.storeId)}>
//                       Store {s.storeId}
//                     </option>
//                   ))}
//                 </select>
//                 <span className="text-sm font-mono text-slate-500 whitespace-nowrap">
//                   Count: {sortedRows.length} Records
//                 </span>
//               </div>
//             </div>

//             {isManagerTab && hasAnyCoMgr && (
//               <div className="text-xs text-slate-600 bg-slate-100 border-b border-slate-200 px-6 py-2">
//                 <span className="font-bold uppercase tracking-wider text-slate-700 mr-1">
//                   Co-manager stores:
//                 </span>
//                 Store sales, quota, and store credits are displayed split (÷2)
//                 per manager row inside this ledger.
//               </div>
//             )}

//             <div className="overflow-auto max-h-[75vh]">
//               <table className="w-full border-collapse font-mono text-base">
//                 <thead>
//                   <tr className="border-b border-slate-200 text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50/70">
//                     <th
//                       colSpan={2}
//                       className="px-4 py-1.5 text-left border-r border-slate-200"
//                     >
//                       Identity Details
//                     </th>
//                     <th
//                       colSpan={4}
//                       className="px-4 py-1.5 text-center border-r border-slate-200"
//                     >
//                       Store Performance Status{" "}
//                       {isManagerTab && hasAnyCoMgr ? " (÷2 Co-Mgr)" : ""}
//                     </th>
//                     <th
//                       colSpan={2}
//                       className="px-4 py-1.5 text-center border-r border-slate-200"
//                     >
//                       Store Credit Pool
//                     </th>
//                     <th
//                       colSpan={3}
//                       className="px-4 py-1.5 text-center border-r border-slate-200"
//                     >
//                       {activeTab === "Manager"
//                         ? "MGR"
//                         : activeTab === "Assistant Manager"
//                           ? "ASST MGR"
//                           : "Personal"}{" "}
//                       Sales
//                     </th>
//                     <th colSpan={1} className="px-4 py-1.5 text-right">
//                       Payout
//                     </th>
//                   </tr>
//                   <tr className="bg-slate-50 border-b border-slate-300 sticky top-0 z-20">
//                     <Th colKey="storeId">Store</Th>
//                     <Th colKey="name">Employee</Th>
//                     <Th colKey="storeSale" right>
//                       Store Sales
//                     </Th>
//                     <Th colKey="quota" right>
//                       Quota
//                     </Th>
//                     <Th colKey="quotaAchieved" right>
//                       Quota %
//                     </Th>
//                     <Th colKey="storeUpDownPercent" right>
//                       Up/Down
//                     </Th>
//                     <Th colKey="tierName">Tier</Th>
//                     <Th colKey="storeCreditRate" right>
//                       Rate
//                     </Th>
//                     <Th colKey="storeCreditAmt" right>
//                       Credit Amt
//                     </Th>
//                     <Th colKey="salesmanSale" right>
//                       Sales Vol
//                     </Th>
//                     <Th colKey="salesmanRate" right>
//                       Rate
//                     </Th>
//                     <Th colKey="salesmanCommissionAmt" right>
//                       Comm Amt
//                     </Th>
//                     <Th colKey="totalCommission" right>
//                       Net Total
//                     </Th>
//                   </tr>
//                 </thead>
//                 <tbody className="divide-y divide-slate-100 text-slate-800">
//                   {sortedRows.length === 0 ? (
//                     <tr>
//                       <td
//                         colSpan={13}
//                         className="px-4 py-14 text-center text-slate-400 text-lg font-sans"
//                       >
//                         No {activeTab.toLowerCase()} accounts returned under
//                         execution criteria.
//                       </td>
//                     </tr>
//                   ) : (
//                     sortedRows.map((r, i) => {
//                       const isHit = Number(r.quotaAchieved) >= 99;
//                       const isCoMgrRow = r.hasCoMgr && isManagerTab;
//                       return (
//                         <tr
//                           key={`${r.storeId}-${r.employeeId || "none"}-${i}`}
//                           className={`transition-colors ${isHit ? "bg-emerald-50/60 hover:bg-emerald-50" : isCoMgrRow ? "bg-slate-100/40 hover:bg-slate-100/70" : "hover:bg-slate-50"} ${r.isPlaceholder ? "opacity-50" : ""}`}
//                         >
//                           <td className="px-4 py-3 whitespace-nowrap">
//                             <div className="flex items-center gap-1.5">
//                               <span className="font-bold text-slate-900">
//                                 {r.storeId}
//                               </span>
//                             </div>
//                           </td>
//                           <td className="px-4 py-3 whitespace-nowrap">
//                             {r.isPlaceholder ? (
//                               <span className="text-sm text-slate-400 italic font-sans">
//                                 Vacant Asset Assignment
//                               </span>
//                             ) : (
//                               <>
//                                 <div
//                                   className={
//                                     !r.name
//                                       ? "text-slate-400 italic font-sans"
//                                       : "font-sans font-medium text-slate-900"
//                                   }
//                                 >
//                                   {r.name || "—"}{" "}
//                                   {isCoMgrRow && !isHit && (
//                                     <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-slate-200 text-slate-600 ml-1">
//                                       co
//                                     </span>
//                                   )}
//                                 </div>
//                                 <div className="text-xs text-slate-400 tracking-wide mt-0.5">
//                                   {r.employeeId}{" "}
//                                   {r.pbsId && `| PBS: ${r.pbsId}`}
//                                 </div>
//                               </>
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-900">
//                             {fmtCur(r.storeSale)}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {fmtCur(r.quota)}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums">
//                             <QuotaText percent={r.quotaAchieved} />
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums">
//                             <UpDownText percent={r.storeUpDownPercent} />
//                           </td>
//                           <td className="px-4 py-3 text-center tabular-nums">
//                             <TierText tier={r.tierName} />
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {r.isPlaceholder || r.storeCreditRate === null ? (
//                               <EmDash />
//                             ) : (
//                               fmtPct(r.storeCreditRate)
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums font-medium text-slate-700">
//                             {r.isPlaceholder ? (
//                               <EmDash />
//                             ) : (
//                               fmtCur(r.storeCreditAmt)
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {r.isPlaceholder ? (
//                               <EmDash />
//                             ) : (
//                               fmtCur(r.salesmanSale)
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {r.isPlaceholder || r.salesmanRate === null ? (
//                               <EmDash />
//                             ) : (
//                               fmtPct(r.salesmanRate)
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                             {r.isPlaceholder ? (
//                               <EmDash />
//                             ) : (
//                               fmtCur(r.salesmanCommissionAmt)
//                             )}
//                           </td>
//                           <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
//                             {fmtCur(r.totalCommission)}
//                           </td>
//                         </tr>
//                       );
//                     })
//                   )}
//                 </tbody>
//                 <tfoot>
//                   <tr className="bg-slate-50/50 border-t-2 border-slate-400 text-slate-900 font-bold">
//                     <td
//                       colSpan={2}
//                       className="px-4 py-3 font-sans text-slate-700 font-bold"
//                     >
//                       Total Aggregates
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums">
//                       {fmtCur(totals.storeSaleSum)}
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                       {fmtCur(totals.quotaSum)}
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums font-extrabold text-slate-950">
//                       {totals.avgQA.toFixed(2)}%
//                     </td>
//                     <td colSpan={2} />
//                     <td className="px-4 py-3 text-right text-xs uppercase text-slate-400 font-sans tracking-wider">
//                       Subtotals
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums text-slate-700">
//                       {fmtCur(totals.storeCreditSum)}
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums text-slate-600">
//                       {fmtCur(totals.salesmanSaleSum)}
//                     </td>
//                     <td colSpan={1} />
//                     <td className="px-4 py-3 text-right tabular-nums text-slate-700">
//                       {fmtCur(totals.salesmanCommSum)}
//                     </td>
//                     <td className="px-4 py-3 text-right tabular-nums text-slate-950 border-b-4 border-double border-slate-900 bg-slate-50">
//                       {fmtCur(totals.totalCommSum)}
//                     </td>
//                   </tr>
//                 </tfoot>
//               </table>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default StoreCommissions;
